import gql from 'graphql-tag';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  AgencyDetails,
  AuthTenant,
  ForgotPasswordErrorType,
  ForgotPasswordResponse,
  InstallerDetails,
  MutationValidateAndUpdateTempPasswordArgs,
  RegisterInvoicePortalUserErrorReason,
  RegisterInvoicePortalUserInput,
  RegisterInvoicePortalUserResponse,
} from '../../__generated__/pge-types';
import useFormState from '../useFormState';
import {
  forgotPasswordRules,
  verifyPasswordRules,
  ForgotPasswordFormModel,
  VerifyPasswordFormModel,
} from '../../components/invoice-payment-portal/auth/forgotPasswordFormValidation.rules';
import useContentMgmt from '../useContentMgmt';
import microcopyGroupIds from '../../components/pge-plus-common/microcopyGroupIds';
import useWrapWithLoader from '../useWrapWithLoading';
import { parsePhoneNumber } from '../../util/format';
import useUtil from './useUtil';
import routes from '../../routes';
import { useMutation } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client';
import useTransition from '../useTransition';
import { ForgotPasswordErrors } from '../useForgotPasswordForm';
import useInvoicePaymentAuth from './useInvoicePaymentAuth';
import { navigate } from 'gatsby';
import { NotificationsContext } from '../../providers/NotificationsProvider';

export const forgotPasswordMutation = gql`
  mutation validateAndUpdateTempPassword($payload: ForgotPasswordRequest) {
    validateAndUpdateTempPassword(payload: $payload) {
      forgotPasswordError
      signInToken
    }
  }
`;

const defaultForgotPasswordFormState: ForgotPasswordFormModel = {
  email: '',
  pinId: '',
};

const defaultVerifyPasswordFormState: VerifyPasswordFormModel = {
  newPassword: '',
  otp: '',
};

export const useInvoicePaymentForgotPassword = () => {
  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.INVOICE_PAYMENT_SIGN_IN,
  );
  const errorCount = useRef(0);

  const { forgotPassword, verifyAndResetPassword } = useInvoicePaymentAuth();

  const { wrapWithLoader } = useWrapWithLoader();

  const forgotPasswordForm = useFormState(defaultForgotPasswordFormState, {
    validate: forgotPasswordRules,
    validationContext: {
      content,
    },
  });

  const verifyPasswordForm = useFormState(defaultVerifyPasswordFormState, {
    validate: verifyPasswordRules,
    validationContext: {
      content,
    },
  });

  const notificationContext = useContext(NotificationsContext);

  const handleError = (errorMessage: any) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleErrorCount();
    notificationContext.setState({
      isOpen: true,
      message: errorMessage,
      severity: 'error',
    });
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const handleSubmit = forgotPasswordForm.submit(
    wrapWithLoader(async () => {
      try {
        await forgotPassword(forgotPasswordForm.values.email);

        void (await navigate(routes.INVOICE_PAYMENT_VERIFY_AND_RESET_PASSWORD, {
          state: {
            email: forgotPasswordForm.values.email,
          },
        }));
      } catch (error) {
        handleError(content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE'));
      }
    }),
  );

  const handleResetSubmit = (email: string) =>
    verifyPasswordForm.submit(
      wrapWithLoader(async () => {
        try {
          await verifyAndResetPassword(
            email,
            verifyPasswordForm.values.otp,
            verifyPasswordForm.values.newPassword,
          );
          notificationContext.setState({
            isOpen: true,
            message: content.get('PASSWORD_RESET_SUCCESSFULLY'),
            severity: 'success',
          });
        } catch (error) {
          handleError(content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE'));
        }
      }),
    );

  const handleErrorCount = () => {
    errorCount.current += 1;
    if (errorCount.current > 2) {
      return navigate(
        routes.INVOICE_PAYMENT_PORTAL_FORGOT_PASSWORD_SPECIAL_HANDLING,
      );
    }
  };

  return {
    forgotPasswordForm,
    verifyPasswordForm,
    content,
    handleSubmit,
    handleResetSubmit,
  };
};
